/**
 * Determines the last non-activity message between store and API messages.
 * @param {Object} messageInStore - The last non-activity message from the store.
 * @param {Object} messageFromAPI - The last non-activity message from the API.
 * @returns {Object} The latest non-activity message.
 */
const getLastNonActivityMessage = (messageInStore, messageFromAPI) => {
  // Ignore reaction messages from API response
  const filteredAPI =
    messageFromAPI && !isReactionMessage(messageFromAPI)
      ? messageFromAPI
      : null;

  if (messageInStore && filteredAPI) {
    return messageInStore.created_at >= filteredAPI.created_at
      ? messageInStore
      : filteredAPI;
  }
  return messageInStore || filteredAPI;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  const lastMessageIncludingActivity = m.messages[m.messages.length - 1];

  const nonActivityMessages = m.messages.filter(
    message =>
      message.message_type !== 2 && !isReactionMessage(message)
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  const lastNonActivityMessageFromAPI = m.last_non_activity_message;

  // If API value and store value for last non activity message
  // is empty, then return the last activity message
  if (!lastNonActivityMessageInStore && !lastNonActivityMessageFromAPI) {
    return lastMessageIncludingActivity;
  }

  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    lastNonActivityMessageFromAPI
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 > agentLastSeenAt * 1000
  );
};

export const isReactionMessage = message => {
  return message.content_attributes?.is_reaction === true;
};

export const separateReactions = (messages = []) => {
  const regularMessages = [];
  const reactionsMap = {};
  // Track which sender+target combos we've already seen (iterating in reverse)
  const seen = {};

  // Reverse pass: first occurrence per sender is the most recent reaction
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!isReactionMessage(message)) continue;

    const targetId = message.content_attributes?.in_reply_to;
    const senderId = message.sender?.id;
    const senderName = message.sender?.name || '';
    if (!targetId || !senderId) continue;

    // GROUP senders stack all reactions (no dedup/removal)
    const isGroup = senderName.includes('(GROUP)');

    if (!isGroup) {
      const key = `${targetId}:${senderId}`;
      if (seen[key]) continue;
      seen[key] = true;

      // If it's a removal, just mark as seen (skips all older reactions from this sender)
      if (message.content_attributes?.remove_reaction) continue;
    }

    if (!reactionsMap[targetId]) {
      reactionsMap[targetId] = [];
    }
    reactionsMap[targetId].push({
      emoji: message.content,
      senderId,
      senderName: message.sender?.name || '',
      messageId: message.id,
    });
  }

  // Forward pass: collect non-reaction messages (preserves order)
  messages.forEach(message => {
    if (!isReactionMessage(message)) {
      regularMessages.push(message);
    }
  });

  return { regularMessages, reactionsMap };
};

export const aggregateReactions = (reactions = []) => {
  const emojiMap = {};
  reactions.forEach(({ emoji, senderName }) => {
    if (!emojiMap[emoji]) {
      emojiMap[emoji] = { emoji, count: 0, senders: [] };
    }
    emojiMap[emoji].count += 1;
    if (senderName && !emojiMap[emoji].senders.includes(senderName)) {
      emojiMap[emoji].senders.push(senderName);
    }
  });
  return Object.values(emojiMap);
};
